/* eslint-disable react/prop-types */
import React, {useEffect, useRef, useState} from 'react'
import {useCompare} from '../../../context/compare'
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Text,
    useDisclosure,
    useStyleConfig,
    useBreakpointValue,
    usePrefersReducedMotion,
} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {ChevronDown, DeleteIcon, PlusIcon} from '../../../components/icons'
import {getImageForProductTile} from '../../../utils/utils'
import CompareModal from './compare-modal'
import {remCalc} from '../../../theme/_utils'

const focusProductListPage = () => {
    const productListPage =
        document.querySelector('[data-testid="sf-product-grid"]') ||
        document.querySelector('.sf-product-grid')

    if (!productListPage) return false

    productListPage.setAttribute('tabindex', '-1')
    productListPage.focus()
    setTimeout(() => productListPage.removeAttribute('tabindex'), 100)
    return true
}

const createEmptySlots = (compareItemsLength, maxCompareItems) =>
    Math.max(maxCompareItems - compareItemsLength, 0)

const ProductSelection = ({product}) => {
    const styles = useStyleConfig('ProductComparePlp')
    const {formatMessage} = useIntl()

    const {removeItem} = useCompare()
    const imgSrc = getImageForProductTile(product)

    return (
        <Flex {...styles.productSelectionContainer}>
            <Flex {...styles.productSelectionSubContainer}>
                <AspectRatio {...styles.productSelectionImage}>
                    <DynamicImage
                        src={`${imgSrc}`}
                        imageProps={{
                            alt: '',
                            loading: 'lazy',
                            mixBlendMode: 'multiply',
                        }}
                    />
                </AspectRatio>
                <Box>
                    <Text {...styles.productSelectionName}>
                        {product?.representedProduct?.c_variantIdentifier ||
                            product?.c_variantIdentifier ||
                            product?.name ||
                            product?.productName}
                    </Text>
                </Box>
            </Flex>
            <Button
                onClick={() => removeItem(product)}
                variant="unstyled"
                {...styles.productSelectionClose}
                data-testid="close-icon"
                aria-label={formatMessage({
                    id: 'country.banner.close',
                    defaultMessage: 'Close',
                })}
            >
                <DeleteIcon boxSize="iconsSize.small" />
            </Button>
        </Flex>
    )
}

const ProductSelectionEmpty = () => {
    const styles = useStyleConfig('ProductComparePlp')

    return (
        <Flex {...styles.productSelectionEmptyContainer}>
            <PlusIcon color="foreground.neutral.dim" width={remCalc(11)} height={remCalc(11)} />
            <Text {...styles?.productSelectionText}>
                <FormattedMessage
                    id="product_list.compare_products.empty_placeholder"
                    defaultMessage="Product to add"
                />
            </Text>
        </Flex>
    )
}

const CompareBanner = ({categoryId}) => {
    const {
        clearItems,
        state: {compareItems},
        compareBannerRef,
    } = useCompare()
    const {isOpen, onOpen, onClose, onToggle} = useDisclosure({defaultIsOpen: false})
    const compareBannerContentRef = useRef(null)
    const closeTimeoutRef = useRef(null)
    const previousCategoryIdRef = useRef(null)
    const [compareBannerContentHeight, setCompareBannerContentHeight] = useState(0)
    const styles = useStyleConfig('ProductComparePlp')

    const clearCloseTimeout = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }
    }

    const handleBannerMouseEnter = () => {
        clearCloseTimeout()
        onOpen()
    }

    const handleBannerMouseLeave = () => {
        clearCloseTimeout()
        closeTimeoutRef.current = setTimeout(() => {
            onClose()
            closeTimeoutRef.current = null
        }, 500)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && isOpen) {
            e.preventDefault()
            clearCloseTimeout()
            onClose()
            setTimeout(() => {
                if (!focusProductListPage()) {
                    compareBannerRef?.current?.blur()
                }
            }, 100)
        }
    }

    const shouldReduceMotion = usePrefersReducedMotion()

    const isMobile = useBreakpointValue({base: true, md: false}) ?? false

    useEffect(() => {
        if (compareBannerContentRef.current) {
            setCompareBannerContentHeight(compareBannerContentRef.current.scrollHeight)
        }
    }, [compareItems, isMobile])

    useEffect(() => {
        return () => {
            clearCloseTimeout()
        }
    }, [])

    useEffect(() => {
        if (!categoryId) return

        if (previousCategoryIdRef.current && previousCategoryIdRef.current !== categoryId) {
            clearItems()
            clearCloseTimeout()
            onClose()
        }

        previousCategoryIdRef.current = categoryId
    }, [categoryId, clearItems, onClose])

    const maxCompareItems = isMobile ? 2 : 3
    return (
        compareItems.length > 0 && (
            <Box
                {...styles.banner}
                ref={compareBannerRef}
                tabIndex={-1}
                data-compare-banner
                onKeyDown={handleKeyDown}
                onMouseEnter={handleBannerMouseEnter}
                onMouseLeave={handleBannerMouseLeave}
                onClick={() => {
                    clearCloseTimeout()
                    onToggle()
                }}
                data-testid="compare-banner-body"
                w={{base: '100%', md: isOpen ? '100%' : '80%'}}
                maxW={remCalc(1320)}
                transition={shouldReduceMotion ? 'none' : 'width 0.6s ease'}
            >
                <Flex w="100%" p={remCalc(16)} flexDir="column" gap={isOpen ? remCalc(16) : 0}>
                    <Flex {...styles.bannerHeader}>
                        <Text {...styles.bannerHeaderLabel}>
                            <FormattedMessage
                                id="product_list.compare_products.label"
                                defaultMessage="Compare {items} of {maxItems}"
                                values={{
                                    items: compareItems.length,
                                    maxItems: maxCompareItems,
                                }}
                            />
                        </Text>
                        <Box onClick={(e) => e.stopPropagation()}>
                            <CompareModal
                                categoryId={categoryId}
                                controlsDisplay={{base: 'none', md: 'flex'}}
                            />
                        </Box>
                        <Button
                            display={{base: 'flex', md: 'none'}}
                            variant="unstyled"
                            onClick={(e) => {
                                e.stopPropagation()
                                onToggle()
                            }}
                            aria-label="Toggle compare banner"
                            minW="auto"
                            h="auto"
                            p={0}
                        >
                            <ChevronDown
                                boxSize="iconsSize.small"
                                color="foreground.neutral.inverse"
                                style={{
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: shouldReduceMotion ? 'none' : 'transform 0.2s ease',
                                }}
                            />
                        </Button>
                    </Flex>
                    <Box
                        ref={compareBannerContentRef}
                        overflow="hidden"
                        maxH={isOpen ? `${compareBannerContentHeight}px` : '0px'}
                        opacity={isOpen ? 1 : 0}
                        transition={
                            shouldReduceMotion ? 'none' : 'max-height 0.6s ease, opacity 0.2s ease'
                        }
                    >
                        <Flex
                            width="100%"
                            alignItems="center"
                            justifyContent={isMobile ? 'flex-start' : 'space-between'}
                            flexDir={isMobile ? 'column' : 'row'}
                            gap={{base: remCalc(6), lg: remCalc(8)}}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {compareItems.map((item) => (
                                <ProductSelection
                                    key={`product-compare-tile-${item.productId}`}
                                    product={item}
                                />
                            ))}
                            {Array.from(
                                {length: createEmptySlots(compareItems.length, maxCompareItems)},
                                (_, index) => (
                                    <ProductSelectionEmpty key={`empty-slot-${index}`} />
                                ),
                            )}
                        </Flex>
                        <Flex
                            display={{base: 'flex', md: 'none'}}
                            mt={remCalc(12)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <CompareModal
                                categoryId={categoryId}
                                controlsDisplay={{base: 'flex', md: 'none'}}
                                showControlsOnMobile
                            />
                        </Flex>
                    </Box>
                </Flex>
            </Box>
        )
    )
}

export default CompareBanner
